import { FC, useMemo, useState } from "react";
import {
  Box,
  createStyles,
  FormControl,
  FormHelperText,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  Typography,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { FormikHelpers, useFormik } from "formik";
import Fuse from "fuse.js";
import { filter, includes, some } from "lodash";
import { Link, useHistory } from "react-router-dom";
import * as Yup from "yup";

import {
  Button,
  ErrorMessage,
  MoneyInput,
  OutlinedTextField,
  TwoNumberInput,
} from "components";
import { removeMoneyMask } from "lib/parse";
import { createOtherSourceSubmission } from "services/otherSourceSubmissions";
import { OtherSourceSubmission } from "services/otherSourceSubmissions/types";
import { useRoyaltyPayors } from "services/royaltyPayor";
import { royaltyPayorKinds } from "services/royaltyPayor/constants";
import { RoyaltyPayor, RoyaltyPayorKind } from "services/royaltyPayor/types";
import theme from "theme";
import { menuItemPurpleStyles, textFieldPurpleStyles } from "theme/constants";

import * as addRoyaltyAccountRoutes from "../routes";
import StepContainer from "./StepContainer";

const useStyles = makeStyles(() =>
  createStyles({
    inputLabel: {
      color: theme.custom.palette.black.main,
      marginTop: theme.spacing(2),
      paddingBottom: theme.spacing(1),
    },
    inputSize: {
      height: "45px",
    },
    inputRootText: textFieldPurpleStyles,
    inputRootSelect: {
      "& .MuiSelect-select:focus": {
        backgroundColor: "unset",
      },
    },
    dropdownMenuItem: menuItemPurpleStyles,
    errorText: {
      color: theme.custom.palette.red.main,
    },
    link: {
      color: theme.custom.palette.blue.main,
      fontSize: "14px",
      "&:hover": {
        color: theme.custom.palette.blue.main,
      },
    },
  }),
);

const royaltyPayorKindsToLabel = [
  { kind: "pro", label: "Performing Rights Organization" },
  { kind: "publisher", label: "Publisher" },
  { kind: "label", label: "Label" },
  { kind: "digital_distributor", label: "Digital Distributor" },
  { kind: "other", label: "Other" },
] as const;

const formSchema = Yup.object().shape<OtherSourceSubmissionFormValues>({
  attested_ltm: Yup.string().required("Required"),
  attested_age: Yup.string().required("Required"),
  biggest_song_name: Yup.string().max(80).label("Song name"),
  biggest_song_artist_name: Yup.string().max(80).label("Artist name"),
  royalty_payor_kind: Yup.string()
    .required("Required")
    .oneOf(royaltyPayorKinds),
  royalty_payor_name: Yup.string()
    .required("Required")
    .max(80)
    .label("Royalty account name"),
});

type OtherSourceSubmissionFormValues = Omit<
  OtherSourceSubmission,
  "id" | "created_at" | "updated_at"
>;
export const initialValues: OtherSourceSubmissionFormValues = {
  attested_ltm: "",
  attested_age: "",
  biggest_song_name: "",
  biggest_song_artist_name: "",
  royalty_payor_kind: "" as RoyaltyPayorKind, // enables initial empty value, formSchema will enforce that it is filled
  royalty_payor_name: "",
};

const OtherSourceSubmissionComponent: FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const royaltyPayors = useRoyaltyPayors().data || [];
  const [error, setError] = useState("");
  const [suggestedRoyaltyPayor, setSuggestedRoyaltyPayor] = useState<
    RoyaltyPayor | undefined
  >();

  const handleSubmit = async (
    formValues: OtherSourceSubmissionFormValues,
    { setSubmitting }: FormikHelpers<OtherSourceSubmissionFormValues>,
  ) => {
    try {
      setSubmitting(true);
      await createOtherSourceSubmission({
        ...formValues,
        attested_ltm: removeMoneyMask(formValues.attested_ltm),
      } as OtherSourceSubmission);
      history.push(addRoyaltyAccountRoutes.otherSourceSubmissionSuccess());
    } catch (e) {
      setError("Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const formik = useFormik<OtherSourceSubmissionFormValues>({
    initialValues,
    validationSchema: formSchema,
    onSubmit: handleSubmit,
  });

  const validRoyaltyPayors = filter(royaltyPayors, royaltyPayor =>
    some(royaltyPayor.permissions, permission =>
      includes(["get_valuation", "get_credentials"], permission.codename),
    ),
  );

  const fuse = useMemo(
    () =>
      new Fuse(validRoyaltyPayors, {
        keys: ["display_name"],
        threshold: 0.2,
        location: 4,
      }),
    [validRoyaltyPayors],
  );

  return (
    <StepContainer>
      <Typography color="primary" variant="h6">
        <strong style={{ fontWeight: theme.typography.fontWeightMedium }}>
          Other Royalty Account Submission
        </strong>
      </Typography>
      <Typography color="primary" align="left">
        Answer a few simple questions about your royalty account and the Royalty
        Exchange team will reach out if you are eligible for offers.
      </Typography>
      <form onSubmit={formik.handleSubmit}>
        {!!suggestedRoyaltyPayor && (
          <Box mt={2}>
            <Alert severity="warning">
              {" "}
              Did you mean to connect an account with{" "}
              {suggestedRoyaltyPayor.display_name}?{" "}
              <Link
                to={
                  some(suggestedRoyaltyPayor.permissions, {
                    codename: "get_valuation",
                  })
                    ? addRoyaltyAccountRoutes.connectGetValuationRoyaltyPayorAccount(
                        suggestedRoyaltyPayor.name,
                      )
                    : addRoyaltyAccountRoutes.connectGetCredentialsRoyaltyPayorAccount(
                        suggestedRoyaltyPayor.name,
                      )
                }
                className={classes.link}
              >
                {" "}
                Click here to connect.
              </Link>
            </Alert>
          </Box>
        )}
        {!!error && <ErrorMessage title="Error" description={error} />}
        <Box mt={2} textAlign="left">
          <OutlinedTextField
            data-attr="other-source-submission-royalty-payor-name"
            autoFocus
            className={classes.inputRootText}
            fullWidth
            name="royalty_payor_name"
            label="Royalty account name"
            type="text"
            value={formik.values.royalty_payor_name}
            onBlur={event => {
              if (formik.values.royalty_payor_name.length >= 2) {
                const result = fuse
                  .search(formik.values.royalty_payor_name)
                  .map(result => result.item);
                setSuggestedRoyaltyPayor(result[0]);
              }

              formik.handleBlur(event);
            }}
            onChange={formik.handleChange}
            error={
              formik.touched.royalty_payor_name &&
              !!formik.errors.royalty_payor_name
            }
            helperText={
              formik.touched.royalty_payor_name &&
              formik.errors.royalty_payor_name
            }
            disabled={formik.isSubmitting}
          />
        </Box>
        <Box textAlign="left">
          <InputLabel className={classes.inputLabel}>
            Royalty account kind
          </InputLabel>
        </Box>
        <Box textAlign="left">
          <FormControl
            data-attr="other-source-submission-royalty-account-kind"
            className={`${classes.inputRootText} ${classes.inputRootSelect}`}
            variant="outlined"
            fullWidth
            size="medium"
          >
            <Select
              fullWidth
              className={classes.inputSize}
              name="royalty_payor_kind"
              value={formik.values.royalty_payor_kind}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.royalty_payor_kind &&
                !!formik.errors.royalty_payor_kind
              }
              disabled={formik.isSubmitting}
            >
              {royaltyPayorKindsToLabel.map((royaltyPayor, index) => (
                <MenuItem
                  className={classes.dropdownMenuItem}
                  value={royaltyPayor.kind}
                  key={index}
                >
                  {royaltyPayor.label}
                </MenuItem>
              ))}
            </Select>
            {formik.touched.royalty_payor_kind &&
              !!formik.errors.royalty_payor_kind && (
                <FormHelperText className={classes.errorText}>
                  {formik.errors.royalty_payor_kind}
                </FormHelperText>
              )}
          </FormControl>
        </Box>
        <Box mt={2} textAlign="left">
          <OutlinedTextField
            data-attr="other-source-submission-attested-ltm"
            fullWidth
            className={classes.inputRootText}
            name="attested_ltm"
            label="Your yearly royalty earnings"
            type="text"
            value={formik.values.attested_ltm}
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            error={formik.touched.attested_ltm && !!formik.errors.attested_ltm}
            helperText={
              formik.touched.attested_ltm && formik.errors.attested_ltm
            }
            InputProps={{ inputComponent: MoneyInput }}
            disabled={formik.isSubmitting}
          />
        </Box>
        <Box mt={2} textAlign="left">
          <OutlinedTextField
            data-attr="other-source-submission-attested-age"
            fullWidth
            className={classes.inputRootText}
            name="attested_age"
            label="How long you've been earning royalties"
            type="text"
            placeholder="3.5"
            value={formik.values.attested_age}
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            error={formik.touched.attested_age && !!formik.errors.attested_age}
            helperText={
              formik.touched.attested_age && formik.errors.attested_age
            }
            InputProps={{
              endAdornment: "years",
              inputComponent: TwoNumberInput,
            }}
            disabled={formik.isSubmitting}
          />
        </Box>
        <Box mt={2} textAlign="left">
          <OutlinedTextField
            data-attr="other-source-submission-biggest-song-name"
            fullWidth
            className={classes.inputRootText}
            name="biggest_song_name"
            label="Name of your biggest song (optional)"
            type="text"
            value={formik.values.biggest_song_name}
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            error={
              formik.touched.biggest_song_name &&
              !!formik.errors.biggest_song_name
            }
            helperText={
              formik.touched.biggest_song_name &&
              formik.errors.biggest_song_name
            }
            disabled={formik.isSubmitting}
          />
        </Box>
        <Box mt={2} textAlign="left">
          <OutlinedTextField
            data-attr="other-source-submission-biggest-song-artist-name"
            fullWidth
            className={classes.inputRootText}
            name="biggest_song_artist_name"
            label="Artist who recorded your biggest song (optional)"
            type="text"
            value={formik.values.biggest_song_artist_name}
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            error={
              formik.touched.biggest_song_artist_name &&
              !!formik.errors.biggest_song_artist_name
            }
            helperText={
              formik.touched.biggest_song_artist_name &&
              formik.errors.biggest_song_artist_name
            }
            disabled={formik.isSubmitting}
          />
        </Box>
        <Box display="flex" flexDirection="column" alignItems="flex-end" mt={4}>
          <Button
            data-attr="other-source-submission-submit-button"
            type="submit"
            customColor="green"
            variant="contained"
            disabled={formik.isSubmitting}
          >
            Next
          </Button>
        </Box>
      </form>
    </StepContainer>
  );
};

export default OtherSourceSubmissionComponent;
